import { Box, Button, Paper, Typography, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useId, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  ChevronLeft as BackIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import {
  borderRadius,
  colorsFoundation,
  dimensions,
  typographyTokens,
} from "@accredo/fe-shared/theme/index.js";

/**
 * V2 version of the Stepper component with two-column layout
 * Left side: Current step content
 * Right side: Progress indicator with all steps
 */
export default function StepperV2(props) {
  const {
    item,
    errors = null,
    renderFields,
    progressMode = "lax",
    actionContext,
    values,
    isSubFormOpen = false,
  } = props;
  const [activeStep, setActiveStep] = useState(0);
  const id = useId();

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const renderActionButtons = () => {
    if (!actionContext || !actionContext.delegateActions) return null;

    const {
      showCancel,
      showDelete,
      submitAction,
      submitAction2,
      cancelLabel,
      handleOnClick,
      handleCancel,
      showYesNoDelete,
      canSave,
      isValid,
    } = actionContext;

    let disableSave = !canSave || isSubFormOpen;
    if (submitAction?.alwaysActive && !isSubFormOpen) disableSave = false;

    const isAction2Active =
      !isSubFormOpen &&
      (submitAction2?.activeWhen &&
      typeof submitAction2.activeWhen === "function"
        ? submitAction2.activeWhen(isValid)
        : canSave);

    return (
      <Box
        sx={{
          mt: 3,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          pt: 2,
          borderTop: `1px solid ${colorsFoundation.neutral[200]}`,
        }}
      >
        {submitAction && (
          <Button
            fullWidth
            disabled={disableSave}
            color={submitAction.color || "primary"}
            variant={submitAction.variant || "contained"}
            onClick={(e) => handleOnClick(e, submitAction)}
            sx={{ textTransform: "none" }}
          >
            speichern
          </Button>
        )}
        {submitAction2 && (
          <Button
            fullWidth
            disabled={!isAction2Active}
            color="primary"
            variant="contained"
            onClick={(e) => handleOnClick(e, submitAction2)}
            sx={{ textTransform: "none" }}
          >
            {submitAction2.label}
          </Button>
        )}
        {showDelete && (
          <Button
            fullWidth
            sx={{ textTransform: "none" }}
            color="error"
            disabled={isSubFormOpen}
            onClick={() => showYesNoDelete()}
          >
            löschen
          </Button>
        )}
        {showCancel && (
          <Button
            fullWidth
            sx={{ textTransform: "none" }}
            color="inherit"
            disabled={isSubFormOpen}
            onClick={() => handleCancel()}
          >
            {cancelLabel}
          </Button>
        )}
      </Box>
    );
  };

  const handleNext = () => {
    if (activeStep < item.steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  function isDefaultValueForItem(item, value) {
    switch (item.control) {
      case "select":
      case "autocomplete":
        return value === (item.default ?? "");

      case "upload":
      case "expandable":
        return Array.isArray(value) && value.length === 0;

      case "switch":
        return value === (item.default ?? false);

      case "datepicker":
      case "radiogroup":
      case "rating":
      case "detailed_radio_group":
        return value === (item.default ?? null);

      case "checkbox":
        return value === (item.default ?? false);

      case "password":
      case "input":
      case "textarea":
        return value === (item.default ?? "");

      case "sliderinput":
        return value === (item.default ?? item.min ?? 0);

      default:
        // fallback: treat falsy as default
        return !value;
    }
  }

  const getErrorCount = (step, step_index) => {
    let num_errors = 0;

    step.children.forEach((child_item) => {
      const has_error = !!errors?.[child_item.name];
      if (!has_error) {
        return;
      }

      const value = values?.[child_item.name];
      if (progressMode === "lax" && step_index === activeStep) {
        const is_default = isDefaultValueForItem(child_item, value);
        if (is_default) {
          return;
        }
      }
      num_errors++;
    });

    return num_errors;
  };

  const isStepComplete = (stepIndex) => {
    // A step is complete if it has been visited and has no errors
    const step = item.steps[stepIndex];
    return (
      getErrorCount(step, stepIndex) === 0 &&
      (progressMode === "full" || stepIndex < activeStep)
    );
  };

  const getStepIcon = (stepIndex) => {
    const step = item.steps[stepIndex];
    const error_count = getErrorCount(step, stepIndex);
    const is_complete = isStepComplete(stepIndex);
    const is_active = stepIndex === activeStep;

    if (
      error_count > 0 &&
      (progressMode === "full" || stepIndex <= activeStep)
    ) {
      return (
        <ErrorIcon
          sx={{
            color: colorsFoundation.key.blue[700],
            fontSize: 24,
          }}
        />
      );
    } else if (is_complete) {
      return (
        <CheckCircleIcon
          sx={{ color: colorsFoundation.status.positive[600], fontSize: 24 }}
        />
      );
    } else if (is_active) {
      return (
        <RadioButtonUncheckedIcon
          sx={{ color: colorsFoundation.key.blue[700], fontSize: 24 }}
        />
      );
    } else {
      return (
        <RadioButtonUncheckedIcon
          sx={{ color: colorsFoundation.neutral[300], fontSize: 24 }}
        />
      );
    }
  };

  const current_step = item.steps[activeStep];

  if (!current_step) {
    return <Box></Box>;
  }
  return (
    <Box sx={{ display: "flex", gap: 3, width: "100%", minHeight: "600px" }}>
      {/* Left side: Current step content */}
      <Box
        sx={{ flex: 1, minWidth: 0 }}
        id={`${id}-step-content-${activeStep}`}
        role="tabpanel"
        aria-labelledby={`${id}-step-tab-${activeStep}`}
      >
        <Paper
          elevation={0}
          sx={{
            height: "100%",
          }}
        >
          {/* Step title */}
          <Typography
            variant="h5"
            sx={{
              textAlign: "left",
              mb: 2,
              fontWeight: typographyTokens.weight.semibold,
              fontSize: typographyTokens.size["2xl"],
            }}
          >
            {current_step?.label}
          </Typography>

          {/* Step description */}
          {current_step.description && (
            <Typography
              variant="body2"
              sx={{
                fontSize: typographyTokens.size.base,
                marginY: dimensions["3xl"],
              }}
            >
              {current_step.description}
            </Typography>
          )}

          {/* Form fields for current step */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {renderFields(current_step.children)}
          </Grid>

          {/* Navigation buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 4,
              justifyContent: "space-between",
            }}
          >
            {activeStep === 0 ? (
              <div></div>
            ) : (
              <Button
                startIcon={<BackIcon />}
                variant="text"
                disabled={activeStep === 0 || isSubFormOpen}
                onClick={handleBack}
                sx={{
                  textTransform: "none",
                  color: colorsFoundation.neutral[700],
                }}
              >
                Zurück
              </Button>
            )}
            {activeStep !== item.steps.length - 1 ? (
              <Button
                variant="contained"
                disabled={activeStep === item.steps.length - 1 || isSubFormOpen}
                onClick={handleNext}
                sx={{ textTransform: "none" }}
              >
                Weiter
              </Button>
            ) : (
              <div></div>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Right side: Progress indicator */}
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          alignSelf: "flex-start",
          position: "sticky",
          top: 84,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            backgroundColor: colorsFoundation.neutral[50],
            borderRadius: borderRadius.sm,
            p: 2,
          }}
        >
          {/* Step list */}
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
            role="tablist"
          >
            {item.steps.map((step, index) => {
              const error_count = getErrorCount(step, index);
              const is_active = index === activeStep;
              const is_complete = isStepComplete(index);
              const has_errors =
                error_count > 0 &&
                (progressMode === "full" || index <= activeStep);
              const tab_id = `${id}-step-tab-${index}`;

              return (
                <Button
                  role="tab"
                  id={tab_id}
                  aria-selected={is_active}
                  aria-controls={`${id}-step-content-${index}`}
                  key={step.label}
                  onClick={() => !isSubFormOpen && handleStepClick(index)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    cursor: isSubFormOpen ? "default" : "pointer",
                    p: 1.5,
                    borderRadius: borderRadius.sm,
                    backgroundColor: is_active
                      ? colorsFoundation.neutral[100]
                      : "transparent",
                    transition: "all 0.2s",
                    opacity: isSubFormOpen ? 0.5 : 1,
                    pointerEvents: isSubFormOpen ? "none" : "auto",
                    "&:hover": {
                      backgroundColor: is_active || isSubFormOpen
                        ? colorsFoundation.neutral[100]
                        : colorsFoundation.neutral[100],
                    },
                  }}
                >
                  {/* Step icon */}
                  <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                    {getStepIcon(index)}
                  </Box>

                  {/* Step label and error count */}
                  <Box sx={{ flex: 1, minWidth: 0, mt: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: typographyTokens.weight.semibold,
                        textAlign: "left",
                        color: colorsFoundation.key.blue[700],
                        wordBreak: "break-word",
                      }}
                    >
                      {step.label}
                    </Typography>
                    {has_errors && (
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: "left",
                          color: colorsFoundation.status.critical[700],
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        {error_count} offen
                      </Typography>
                    )}
                    {is_complete && (
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: "left",
                          color: colorsFoundation.status.positive[600],
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        Abgeschlossen
                      </Typography>
                    )}
                  </Box>
                </Button>
              );
            })}
          </Box>
          {renderActionButtons()}
        </Paper>
      </Box>
    </Box>
  );
}

StepperV2.propTypes = {
  item: PropTypes.object.isRequired,
  value: PropTypes.any,
  error: PropTypes.string,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  showErrorText: PropTypes.bool,
  variant: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  renderFields: PropTypes.func,
  handleRemoveValues: PropTypes.func,
  validateForm: PropTypes.func,
  actionContext: PropTypes.shape({
    showCancel: PropTypes.bool,
    showDelete: PropTypes.bool,
    submitAction: PropTypes.object,
    submitAction2: PropTypes.object,
    cancelLabel: PropTypes.string,
    handleOnClick: PropTypes.func,
    handleCancel: PropTypes.func,
    showYesNoDelete: PropTypes.func,
    canSave: PropTypes.bool,
    isValid: PropTypes.bool,
    delegateActions: PropTypes.bool,
  }),
};
